import { create } from 'zustand';
import api from '../utils/api';

const getSafeJSON = (key) => {
  try {
    const item = sessionStorage.getItem(key);
    if (!item || item === 'undefined') return null;
    return JSON.parse(item);
  } catch (e) {
    console.error(`Error parsing sessionStorage key "${key}":`, e);
    return null;
  }
};

const useAuthStore = create((set, get) => ({
  user: getSafeJSON('usuario'),
  tenant: getSafeJSON('tenant'),
  acl: getSafeJSON('acl'),
  vistas: getSafeJSON('vistas'),
  token: sessionStorage.getItem('token') || null,
  isAuthenticated: !!sessionStorage.getItem('token'),
  loading: false,
  error: null,

  // Login para Tenants (Email, Password, Tenant Slug)
  loginTenant: async (email, password, tenant_slug) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post('/v1/auth/login-tenant', { email, password, tenant_slug });

      const { token, refresh_token, user, tenant, acl, vistas } = response.data.data;

      sessionStorage.setItem('token', token);
      sessionStorage.setItem('refresh_token', refresh_token);
      sessionStorage.setItem('usuario', JSON.stringify(user));
      sessionStorage.setItem('tenant', JSON.stringify(tenant));
      sessionStorage.setItem('acl', JSON.stringify(acl));
      sessionStorage.setItem('vistas', JSON.stringify(vistas));

      set({
        user,
        tenant,
        acl,
        vistas,
        token,
        isAuthenticated: true,
        loading: false
      });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Error al iniciar sesión', loading: false });
      throw error;
    }
  },

  // Login por Referencia (Email + Referencia)
  loginReference: async (email, reference) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post('/v1/auth/login-reference', { email, reference });
      const { token, refresh_token, user, tenant, acl, vistas } = response.data.data;

      sessionStorage.setItem('token', token);
      sessionStorage.setItem('refresh_token', refresh_token);
      sessionStorage.setItem('usuario', JSON.stringify(user));
      sessionStorage.setItem('tenant', JSON.stringify(tenant));
      sessionStorage.setItem('acl', JSON.stringify(acl));
      sessionStorage.setItem('vistas', JSON.stringify(vistas));

      set({
        user,
        tenant,
        acl,
        vistas,
        token,
        isAuthenticated: true,
        loading: false
      });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Error en validación de referencia', loading: false });
      throw error;
    }
  },

  // Login con Google
  loginGoogle: async (credential) => {
    set({ loading: true, error: null });
    try {
      const response = await api.post('/v1/auth/login-google', { credential });
      // El backend (ControllerLogin::loginGoogle) devuelve el usuario bajo la clave "usuario"
      const { token, refresh_token, usuario: user, tenant, acl, vistas } = response.data;

      sessionStorage.setItem('token', token);
      sessionStorage.setItem('refresh_token', refresh_token);
      sessionStorage.setItem('usuario', JSON.stringify(user));
      sessionStorage.setItem('tenant', JSON.stringify(tenant));
      sessionStorage.setItem('acl', JSON.stringify(acl));
      sessionStorage.setItem('vistas', JSON.stringify(vistas));

      set({
        user,
        tenant,
        acl,
        vistas,
        token,
        isAuthenticated: true,
        loading: false
      });
      return response.data;
    } catch (error) {
      set({ error: 'Error en autenticación con Google', loading: false });
      throw error;
    }
  },

  logout: () => {
    sessionStorage.clear();
    set({ user: null, tenant: null, acl: null, vistas: null, token: null, isAuthenticated: false });
    globalThis.location.href = '/login';
  },

  fetchMe: async () => {
    try {
      const response = await api.get('/v1/auth/me');
      const { user, tenant, acl, vistas } = response.data.data;

      sessionStorage.setItem('usuario', JSON.stringify(user));
      sessionStorage.setItem('tenant', JSON.stringify(tenant));
      sessionStorage.setItem('acl', JSON.stringify(acl));
      sessionStorage.setItem('vistas', JSON.stringify(vistas));

      set({ user, tenant, acl, vistas });
    } catch (error) {
      console.error('Error fetching user info', error);
      if (error.response?.status === 401) {
        get().logout();
      }
    }
  },

  // Renueva el access token usando el refresh token almacenado.
  // Devuelve el nuevo access token; lanza si el refresh falla (el interceptor hace logout).
  refreshToken: async () => {
    const refresh_token = sessionStorage.getItem('refresh_token');
    if (!refresh_token) throw new Error('No hay refresh token');

    const response = await api.post('/v1/auth/refresh', { refresh_token });
    const data = response.data?.data || {};
    const { token, refresh_token: newRefresh, user, tenant, acl, vistas } = data;

    if (!token) throw new Error('Respuesta de refresh sin token');

    sessionStorage.setItem('token', token);
    if (newRefresh) sessionStorage.setItem('refresh_token', newRefresh);
    if (user) sessionStorage.setItem('usuario', JSON.stringify(user));
    if (tenant !== undefined) sessionStorage.setItem('tenant', JSON.stringify(tenant));
    if (acl) sessionStorage.setItem('acl', JSON.stringify(acl));
    if (vistas) sessionStorage.setItem('vistas', JSON.stringify(vistas));

    set({
      token,
      isAuthenticated: true,
      ...(user ? { user } : {}),
      ...(tenant !== undefined ? { tenant } : {}),
      ...(acl ? { acl } : {}),
      ...(vistas ? { vistas } : {}),
    });

    return token;
  }
}));

export default useAuthStore;
